"use client";

import { c as _c } from "react/compiler-runtime";
interface MarketSuccessRate {
  market: string;
  successRatePercent: number;
}
interface PredictionStatsProps {
  overallSuccessRate: number;
  byMarket: MarketSuccessRate[];
  single: {
    predictionsMade: number;
    correct: number;
    points: number;
  };
  topX: {
    participations: number;
    points: number;
  };
  matchQuiz: {
    participations: number;
    points: number;
  };
  labels: Record<string, string>;
}
const MARKET_LABEL_MAP: Record<string, {
  key: string;
  fallback: string;
}> = {
  FT_1X2: {
    key: "marketFT1X2",
    fallback: "Full Time Result"
  },
  HT_1X2: {
    key: "marketHT1X2",
    fallback: "Half Time Result"
  },
  HT_FT: {
    key: "marketHTFT",
    fallback: "Half Time / Full Time"
  },
  CORRECT_SCORE: {
    key: "marketCorrectScore",
    fallback: "Correct Score"
  },
  CORRECT_SCORE_HT: {
    key: "marketCorrectScoreHT",
    fallback: "Correct Score (HT)"
  },
  CORRECT_SCORE_ADVANCED: {
    key: "marketCorrectScoreAdvanced",
    fallback: "Correct Score Advanced"
  },
  BTTS: {
    key: "marketBTTS",
    fallback: "Both Teams to Score"
  },
  OVER_UNDER: {
    key: "marketOverUnder",
    fallback: "Over/Under"
  },
  DOUBLE_CHANCE: {
    key: "marketDoubleChance",
    fallback: "Double Chance"
  },
  PLAYER_GOALSCORER: {
    key: "marketPlayerGoalscorer",
    fallback: "Player Goalscorer"
  }
};
function formatMarketName(market: string, labels: Record<string, string>): string {
  const mapping = MARKET_LABEL_MAP[market];
  if (mapping) {
    return labels[mapping.key] || mapping.fallback;
  }
  // Default: replace underscores with spaces, title case
  return market.replace(/_/g, " ").toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
}
export default function PredictionStats(t0) {
  const $ = _c(104);
  const {
    overallSuccessRate,
    byMarket,
    single,
    topX,
    matchQuiz,
    labels
  } = t0;
  let t1;
  if ($[0] !== overallSuccessRate) {
    t1 = Math.round(overallSuccessRate);
    $[0] = overallSuccessRate;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const percent = t1;
  let t2;
  if ($[2] !== labels.predictionStats) {
    t2 = <h2 className="text-xl font-bold text-foreground">{labels.predictionStats}</h2>;
    $[2] = labels.predictionStats;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const t3 = `conic-gradient(var(--primary) ${percent}%, transparent ${percent}%)`;
  let t4;
  if ($[4] !== t3) {
    t4 = {
      background: t3
    };
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== percent) {
    t5 = <div className="flex h-24 w-24 items-center justify-center rounded-full bg-card"><span className="text-3xl font-bold text-foreground">{percent}%</span></div>;
    $[6] = percent;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== t4 || $[9] !== t5) {
    t6 = <div className="relative flex h-32 w-32 items-center justify-center rounded-full" style={t4}>{t5}</div>;
    $[8] = t4;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== labels.overallSuccessRate) {
    t7 = <p className="text-sm font-medium text-muted-foreground">{labels.overallSuccessRate}</p>;
    $[11] = labels.overallSuccessRate;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== t6 || $[14] !== t7) {
    t8 = <div className="flex flex-col items-center gap-2">{t6}{t7}</div>;
    $[13] = t6;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== byMarket || $[17] !== labels) {
    t9 = byMarket.length > 0 && <div className="flex w-full flex-1 flex-col gap-3"><h3 className="text-sm font-semibold text-foreground">{labels.byMarket}</h3><div className="flex flex-col gap-2">{byMarket.map(m => <div key={m.market} className="flex items-center gap-3"><span className="w-36 shrink-0 truncate text-sm text-muted-foreground">{formatMarketName(m.market, labels)}</span><div className="relative h-2.5 flex-1 overflow-hidden rounded-full bg-muted"><div className="h-full rounded-full bg-primary transition-all duration-300" style={{
              width: `${Math.round(m.successRatePercent)}%`
            }} /></div><span className="w-10 text-right text-sm font-medium text-foreground">{Math.round(m.successRatePercent)}%</span></div>)}</div></div>;
    $[16] = byMarket;
    $[17] = labels;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== t8 || $[20] !== t9) {
    t10 = <div className="flex flex-col items-center gap-6 md:flex-row md:items-start">{t8}{t9}</div>;
    $[19] = t8;
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== labels.singlePredictions) {
    t11 = <p className="text-sm font-semibold text-foreground">{labels.singlePredictions}</p>;
    $[22] = labels.singlePredictions;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  const t12 = labels.predictionsMade || "Predictions";
  let t13;
  if ($[24] !== t12) {
    t13 = <span>{t12}</span>;
    $[24] = t12;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== single.predictionsMade) {
    t14 = <span className="font-medium text-foreground">{single.predictionsMade}</span>;
    $[26] = single.predictionsMade;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] !== t13 || $[29] !== t14) {
    t15 = <div className="flex justify-between">{t13}{t14}</div>;
    $[28] = t13;
    $[29] = t14;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  const t16 = labels.correct || "Correct";
  let t17;
  if ($[31] !== t16) {
    t17 = <span>{t16}</span>;
    $[31] = t16;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  let t18;
  if ($[33] !== single.correct) {
    t18 = <span className="font-medium text-foreground">{single.correct}</span>;
    $[33] = single.correct;
    $[34] = t18;
  } else {
    t18 = $[34];
  }
  let t19;
  if ($[35] !== t17 || $[36] !== t18) {
    t19 = <div className="flex justify-between">{t17}{t18}</div>;
    $[35] = t17;
    $[36] = t18;
    $[37] = t19;
  } else {
    t19 = $[37];
  }
  const t20 = labels.points || "Points";
  let t21;
  if ($[38] !== t20) {
    t21 = <span>{t20}</span>;
    $[38] = t20;
    $[39] = t21;
  } else {
    t21 = $[39];
  }
  let t22;
  if ($[40] !== single.points) {
    t22 = <span className="font-medium text-primary">{single.points}</span>;
    $[40] = single.points;
    $[41] = t22;
  } else {
    t22 = $[41];
  }
  let t23;
  if ($[42] !== t21 || $[43] !== t22) {
    t23 = <div className="flex justify-between">{t21}{t22}</div>;
    $[42] = t21;
    $[43] = t22;
    $[44] = t23;
  } else {
    t23 = $[44];
  }
  let t24;
  if ($[45] !== t15 || $[46] !== t19 || $[47] !== t23) {
    t24 = <div className="mt-2 flex flex-col gap-1 text-sm text-muted-foreground">{t15}{t19}{t23}</div>;
    $[45] = t15;
    $[46] = t19;
    $[47] = t23;
    $[48] = t24;
  } else {
    t24 = $[48];
  }
  let t25;
  if ($[49] !== t11 || $[50] !== t24) {
    t25 = <div className="rounded-lg border border-border bg-card p-4">{t11}{t24}</div>;
    $[49] = t11;
    $[50] = t24;
    $[51] = t25;
  } else {
    t25 = $[51];
  }
  let t26;
  if ($[52] !== labels.topXGames) {
    t26 = <p className="text-sm font-semibold text-foreground">{labels.topXGames}</p>;
    $[52] = labels.topXGames;
    $[53] = t26;
  } else {
    t26 = $[53];
  }
  const t27 = labels.participations || "Participations";
  let t28;
  if ($[54] !== t27) {
    t28 = <span>{t27}</span>;
    $[54] = t27;
    $[55] = t28;
  } else {
    t28 = $[55];
  }
  let t29;
  if ($[56] !== topX.participations) {
    t29 = <span className="font-medium text-foreground">{topX.participations}</span>;
    $[56] = topX.participations;
    $[57] = t29;
  } else {
    t29 = $[57];
  }
  let t30;
  if ($[58] !== t28 || $[59] !== t29) {
    t30 = <div className="flex justify-between">{t28}{t29}</div>;
    $[58] = t28;
    $[59] = t29;
    $[60] = t30;
  } else {
    t30 = $[60];
  }
  const t31 = labels.points || "Points";
  let t32;
  if ($[61] !== t31) {
    t32 = <span>{t31}</span>;
    $[61] = t31;
    $[62] = t32;
  } else {
    t32 = $[62];
  }
  let t33;
  if ($[63] !== topX.points) {
    t33 = <span className="font-medium text-primary">{topX.points}</span>;
    $[63] = topX.points;
    $[64] = t33;
  } else {
    t33 = $[64];
  }
  let t34;
  if ($[65] !== t32 || $[66] !== t33) {
    t34 = <div className="flex justify-between">{t32}{t33}</div>;
    $[65] = t32;
    $[66] = t33;
    $[67] = t34;
  } else {
    t34 = $[67];
  }
  let t35;
  if ($[68] !== t30 || $[69] !== t34) {
    t35 = <div className="mt-2 flex flex-col gap-1 text-sm text-muted-foreground">{t30}{t34}</div>;
    $[68] = t30;
    $[69] = t34;
    $[70] = t35;
  } else {
    t35 = $[70];
  }
  let t36;
  if ($[71] !== t26 || $[72] !== t35) {
    t36 = <div className="rounded-lg border border-border bg-card p-4">{t26}{t35}</div>;
    $[71] = t26;
    $[72] = t35;
    $[73] = t36;
  } else {
    t36 = $[73];
  }
  let t37;
  if ($[74] !== labels.matchQuizGames) {
    t37 = <p className="text-sm font-semibold text-foreground">{labels.matchQuizGames}</p>;
    $[74] = labels.matchQuizGames;
    $[75] = t37;
  } else {
    t37 = $[75];
  }
  const t38 = labels.participations || "Participations";
  let t39;
  if ($[76] !== t38) {
    t39 = <span>{t38}</span>;
    $[76] = t38;
    $[77] = t39;
  } else {
    t39 = $[77];
  }
  let t40;
  if ($[78] !== matchQuiz.participations) {
    t40 = <span className="font-medium text-foreground">{matchQuiz.participations}</span>;
    $[78] = matchQuiz.participations;
    $[79] = t40;
  } else {
    t40 = $[79];
  }
  let t41;
  if ($[80] !== t39 || $[81] !== t40) {
    t41 = <div className="flex justify-between">{t39}{t40}</div>;
    $[80] = t39;
    $[81] = t40;
    $[82] = t41;
  } else {
    t41 = $[82];
  }
  const t42 = labels.points || "Points";
  let t43;
  if ($[83] !== t42) {
    t43 = <span>{t42}</span>;
    $[83] = t42;
    $[84] = t43;
  } else {
    t43 = $[84];
  }
  let t44;
  if ($[85] !== matchQuiz.points) {
    t44 = <span className="font-medium text-primary">{matchQuiz.points}</span>;
    $[85] = matchQuiz.points;
    $[86] = t44;
  } else {
    t44 = $[86];
  }
  let t45;
  if ($[87] !== t43 || $[88] !== t44) {
    t45 = <div className="flex justify-between">{t43}{t44}</div>;
    $[87] = t43;
    $[88] = t44;
    $[89] = t45;
  } else {
    t45 = $[89];
  }
  let t46;
  if ($[90] !== t41 || $[91] !== t45) {
    t46 = <div className="mt-2 flex flex-col gap-1 text-sm text-muted-foreground">{t41}{t45}</div>;
    $[90] = t41;
    $[91] = t45;
    $[92] = t46;
  } else {
    t46 = $[92];
  }
  let t47;
  if ($[93] !== t37 || $[94] !== t46) {
    t47 = <div className="rounded-lg border border-border bg-card p-4">{t37}{t46}</div>;
    $[93] = t37;
    $[94] = t46;
    $[95] = t47;
  } else {
    t47 = $[95];
  }
  let t48;
  if ($[96] !== t25 || $[97] !== t36 || $[98] !== t47) {
    t48 = <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">{t25}{t36}{t47}</div>;
    $[96] = t25;
    $[97] = t36;
    $[98] = t47;
    $[99] = t48;
  } else {
    t48 = $[99];
  }
  let t49;
  if ($[100] !== t10 || $[101] !== t2 || $[102] !== t48) {
    t49 = <div className="flex flex-col gap-6">{t2}{t10}{t48}</div>;
    $[100] = t10;
    $[101] = t2;
    $[102] = t48;
    $[103] = t49;
  } else {
    t49 = $[103];
  }
  return t49;
}